import { UIPhoneInput } from '@/components/core';
import { Button } from '@/components/ui-kit/button';
import {
  DialogCancelButton,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui-kit/dialog';
import { Form, FormControl, FormField, FormItem, FormMessage } from '@/components/ui-kit/form';
import { Input } from '@/components/ui-kit/input';
import { Label } from '@/components/ui-kit/label';
import { Separator } from '@/components/ui-kit/separator';
import { ModuleName } from '@/constant/modules.constants';
import { useToast } from '@/hooks/use-toast';
import { uploadPublicFile } from '@/lib/api/services/storage.service';
import {
  Employee,
  EmployeesPagingResult,
  useGetMyEmployee,
  useProfileCompletion,
  useUpdateMyAccount,
} from '@/modules/hr-directory';
import { User } from '@/types/user.type';
import { Trash, Upload } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { isPossiblePhoneNumber, isValidPhoneNumber, Value } from 'react-phone-number-input';
import { useNavigate } from 'react-router-dom';
import { ImageCropper } from '../image-cropper/image-cropper';
import { useReportUnreportedError } from '@/state/query-client/hooks';
import { APP_PATHS } from '@/constant/app-paths';

const DummyProfile = '/assets/images/dummy_profile.png';

/**
 * `EditProfile` component allows the user to edit their profile details, including their first and last name, email, phone number, and profile image.
 * It provides a form to upload a new profile image, edit personal information, and validates phone number input.
 * The save goes to the login user (IAM) first and then to the person's HR profile, the same path as
 * the "Edit profile" Overview tab, so both always carry the same name, phone and photo.
 *
 * @component
 * @example
 * const userInfo = {
 *   firstName: 'John',
 *   lastName: 'Doe',
 *   email: 'john.doe@example.com',
 *   phoneNumber: '+1234567890',
 *   profileImageUrl: 'http://example.com/profile.jpg',
 *   itemId: '12345'
 * };
 *
 * <EditProfile userInfo={userInfo} onClose={() => {}} />
 *
 * @param {Object} props - The component's props
 * @param {User} props.userInfo - The user information object containing current details to be edited
 * @param {Function} props.onClose - Callback function to close the dialog/modal
 *
 * @returns {React.Element} The rendered component
 */

type FormData = {
  itemId: string;
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  profileImageUrl: File | string;
  /** Storage file id of a photo picked in this dialog; `undefined` while the photo is unchanged. */
  profileImageId?: string;
};

type EditProfileProps = {
  userInfo: User;
  onClose: () => void;
};

export function EditProfile({ userInfo, onClose }: Readonly<EditProfileProps>) {
  const [previewImage, setPreviewImage] = useState<string>(DummyProfile);
  const [isFormChanged, setIsFormChanged] = useState(false);
  const [showCropper, setShowCropper] = useState(false);
  const [imageToCrop, setImageToCrop] = useState<string>('');
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { toast } = useToast();

  // The person's HR profile mirrors their name, phone and photo, so it is saved with the account.
  const { data: myEmployee } = useGetMyEmployee();
  const employee: Employee | undefined = (myEmployee as { Employees?: EmployeesPagingResult })
    ?.Employees?.items?.[0];
  // Same phone rule as the "Edit profile" Contact Info tab: required only for an extended profile.
  const { requiresExtendedInfo } = useProfileCompletion();
  const { mutateAsync: updateMyAccount, isPending } = useUpdateMyAccount();
  const reportError = useReportUnreportedError();

  const form = useForm<FormData>({
    defaultValues: {
      itemId: '',
      firstName: '',
      lastName: '',
      email: '',
      phoneNumber: '',
      profileImageUrl: '',
    },
  });

  const { watch, setValue, handleSubmit, control } = form;
  const watchedValues = watch();

  useEffect(() => {
    if (userInfo) {
      setValue('firstName', userInfo.firstName ?? '');
      setValue('lastName', userInfo.lastName ?? '');
      setValue('email', userInfo.email ?? '');
      setValue('phoneNumber', userInfo.phoneNumber ?? '');
      setValue('itemId', userInfo.itemId ?? '');
      setValue('profileImageUrl', userInfo.profileImageUrl ?? '');
      setPreviewImage(userInfo.profileImageUrl || DummyProfile);
    }
  }, [userInfo, setValue]);

  useEffect(() => {
    const initialValues = {
      firstName: userInfo.firstName ?? '',
      lastName: userInfo.lastName ?? '',
      phoneNumber: userInfo.phoneNumber ?? '',
      profileImageUrl: userInfo.profileImageUrl ?? '',
    };

    setIsFormChanged(
      watchedValues.firstName !== initialValues.firstName ||
        watchedValues.lastName !== initialValues.lastName ||
        watchedValues.phoneNumber !== initialValues.phoneNumber ||
        watchedValues.profileImageUrl !== initialValues.profileImageUrl
    );
  }, [watchedValues, userInfo]);

  const uploadProfileImage = async (file: File) => {
    try {
      setIsUploadingImage(true);
      return await uploadPublicFile(file, ModuleName.DefaultConstruct);
    } catch (error) {
      toast({
        title: t('UPLOAD_FAILED'),
        description: t('ERROR_OCCURRED_WHILE_UPLOADING_IMAGE'),
      });
      return null;
    } finally {
      setIsUploadingImage(false);
    }
  };

  const onSubmit = async (data: FormData) => {
    const photoChanged = data.profileImageId !== undefined;
    try {
      await updateMyAccount({
        employee,
        details: {
          FirstName: data.firstName.trim(),
          LastName: data.lastName.trim(),
          PhoneNumber: data.phoneNumber,
          ...(photoChanged
            ? {
                ProfileImageId: data.profileImageId,
                ProfileImageUrl:
                  typeof data.profileImageUrl === 'string' ? data.profileImageUrl : '',
              }
            : {}),
        },
      });
    } catch (error) {
      reportError(error); // the dialog stays open for a retry
      return;
    }
    onClose();
    navigate(APP_PATHS.MY_PROFILE);
  };

  const handleRemoveImage = () => {
    setValue('profileImageUrl', '');
    setValue('profileImageId', '');
    setPreviewImage(DummyProfile);
  };

  const onDrop = (acceptedFiles: File[]) => {
    const file = acceptedFiles[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = () => {
      setImageToCrop(reader.result as string);
      setShowCropper(true);
    };
    reader.readAsDataURL(file);
  };

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    accept: {
      'image/jpeg': [],
      'image/png': [],
    },
    multiple: false,
  });

  return (
    <DialogContent className="grid-cols-[minmax(0,1fr)] rounded-md sm:max-w-[700px] overflow-y-auto">
      <DialogHeader>
        <DialogTitle>{t('EDIT_PROFILE_DETAILS')}</DialogTitle>
        <DialogDescription>{t('KEEP_DETAILS_ACCURATE_UP_TO_DATE')}</DialogDescription>
      </DialogHeader>
      <Form {...form}>
        <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-5">
          <div className="flex items-start sm:items-center">
            <img
              src={previewImage}
              alt="Profile"
              className="w-[90px] h-[90px] sm:w-[100px] sm:h-[100px] shrink-0 rounded-full object-cover border shadow-sm"
            />
            <div className="flex min-w-0 flex-col gap-2 ml-4 sm:ml-9">
              <h1 className="text-xl font-semibold break-words">
                {userInfo.firstName} {userInfo.lastName}
              </h1>
              <p className="text-sm">{t('FILE_FORMAT_AND_SIZE')}</p>
              <div className="flex flex-wrap gap-2 sm:gap-4">
                <div {...getRootProps()} className="inline-block">
                  <Button size="sm" variant="outline" type="button">
                    <Upload className="w-4 h-4" />
                    <Label className="text-xs font-medium cursor-pointer">
                      {t('UPLOAD_IMAGE')}
                    </Label>
                    <input {...getInputProps()} className="hidden" />
                  </Button>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  type="button"
                  onClick={handleRemoveImage}
                  className="text-destructive hover:text-destructive"
                >
                  <Trash className="w-4 h-4" />
                  {t('REMOVE')}
                </Button>
              </div>
            </div>
          </div>
          <Separator />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <FormField
              control={control}
              name="firstName"
              rules={{ validate: (value) => Boolean(value.trim()) || t('ERR_MSG_REQUIRED') }}
              render={({ field }) => (
                <FormItem>
                  <Label>{t('FIRST_NAME')} *</Label>
                  <FormControl>
                    <Input {...field} placeholder={t('FIRST_NAME')} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={control}
              name="lastName"
              rules={{ validate: (value) => Boolean(value.trim()) || t('ERR_MSG_REQUIRED') }}
              render={({ field }) => (
                <FormItem>
                  <Label>{t('LAST_NAME')} *</Label>
                  <FormControl>
                    <Input {...field} placeholder={t('LAST_NAME')} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <Label>{t('EMAIL')}</Label>
                  <FormControl>
                    <Input {...field} disabled />
                  </FormControl>
                </FormItem>
              )}
            />
            <FormField
              control={control}
              name="phoneNumber"
              rules={{
                validate: (value) => {
                  if (!value) return requiresExtendedInfo ? t('PHONE_NUMBER_REQUIRED') : true;
                  if (!isPossiblePhoneNumber(value)) return t('PHONE_NUMBER_LENGTH_INVALID');
                  if (!isValidPhoneNumber(value)) return t('INVALID_PHONE_NUMBER');
                  return true;
                },
              }}
              render={({ field }) => (
                <FormItem>
                  <Label>
                    {t('MOBILE_NO')}
                    {requiresExtendedInfo && ' *'}
                  </Label>
                  <FormControl>
                    <UIPhoneInput
                      {...field}
                      onChange={(value: Value) => setValue('phoneNumber', value ?? '')}
                      placeholder={t('ENTER_YOUR_MOBILE_NUMBER')}
                      defaultCountry="CH"
                      countryCallingCodeEditable={false}
                      international
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
          <DialogFooter className="mt-5 flex justify-end gap-2">
            <DialogCancelButton onClick={onClose}>{t('CANCEL')}</DialogCancelButton>
            <Button
              type="submit"
              loading={isPending}
              disabled={
                isPending ||
                !isFormChanged ||
                !form.getValues('firstName').trim() ||
                !form.getValues('lastName').trim()
              }
            >
              {isPending ? t('SAVING') : t('SAVE')}
            </Button>
          </DialogFooter>
        </form>
      </Form>

      {showCropper && (
        <ImageCropper
          image={imageToCrop}
          onClose={() => setShowCropper(false)}
          isUploading={isUploadingImage}
          onCropComplete={async (croppedImage) => {
            try {
              const res = await fetch(croppedImage);
              const blob = await res.blob();

              if (blob.size > 2 * 1024 * 1024) {
                toast({
                  title: t('IMAGE_TOO_LARGE'),
                  description: t('CROPPED_IMAGE_TOO_LARGE'),
                  variant: 'destructive',
                });
                return;
              }

              const file = new File([blob], 'profile.jpg', { type: 'image/jpeg' });

              // Upload image immediately after cropping
              const uploaded = await uploadProfileImage(file);

              if (uploaded) {
                // Store the uploaded URL (IAM) and its file id (the HR profile) instead of the file
                setValue('profileImageUrl', uploaded.fileUrl);
                setValue('profileImageId', uploaded.fileId);
                setPreviewImage(croppedImage);
                setShowCropper(false);
              }
            } catch (error) {
              toast({
                title: t('UPLOAD_FAILED'),
                description: t('ERROR_OCCURRED_WHILE_UPLOADING_IMAGE'),
                variant: 'destructive',
              });
            }
          }}
          aspect={1}
        />
      )}
    </DialogContent>
  );
}
